import { useCallback } from 'react'
import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { trpc } from 'api'
import { router } from 'expo-router'
import { colors } from '@ui/colors'
import * as Sentry from '@sentry/react-native'
import { useToast } from '@ui/toasts/useToastManager'
import { debugOrder } from '@order/utils/getDebugLog'
import Button from '@ui/buttons/Button'
import usePayments, { PaymentDetails } from '@order/hooks/usePayments'
import { useModalState } from '@order/hooks/useModalDataState'
import ConfirmationModal from '@ui/components/ConfirmationModal'
import { usePaymentMethodSettings } from '@order/hooks/usePaymentMethods'

export default function CancelButton({ payment }: { payment: PaymentDetails }) {
  const { t } = useTranslation(['screens', 'modals'])
  const toast = useToast()
  const confirm = useModalState()

  const handleCancelPayment = usePayments((s) => s.handleCancelPayment)

  const settings = usePaymentMethodSettings(payment.method, {
    provider: payment.provider,
  })

  const cancelPayment = trpc.order.payments.cancel.useMutation({
    onSuccess: () => {
      handleCancelPayment(payment.localId)
      router.replace(`/payment/settings/${payment.localId}`)
    },
    onError: (error) => {
      debugOrder.log({ error })
      Sentry.captureException(error)
      toast({
        title: t('toasts:error.default.desc'),
        text: error?.message || t('toasts:error.default.desc'),
        variant: 'error',
      })
    },
  })

  const handleCancel = useCallback(() => {
    if (!payment.paymentId || !payment.provider) {
      return
    }
    cancelPayment.mutate({
      payment_id: payment.paymentId,
      method: payment.method,
      provider: payment.provider,
    })
    confirm.close()
  }, [cancelPayment, confirm, payment])

  const canCancel =
    payment.method === 'terminal' &&
    payment.status === 'waiting' &&
    !!payment.provider &&
    settings.canCancel === true

  if (!canCancel) return null

  return (
    <View className="items-center p-8">
      <Button
        loading={cancelPayment.isPending}
        overrideColorFront={colors.red[700]}
        colors={[colors.white, colors.red[100]]}
        className="border-red-700"
        variant="outlined"
        label={t('screens:completed.payment.cancel')}
        onPress={confirm.open}
      />
      <ConfirmationModal
        open={confirm.isOpen}
        onClose={confirm.close}
        onConfirm={handleCancel}
        isPending={cancelPayment.isPending}
        title={t('modals:cancelPayment.title')}
        desc={t('modals:cancelPayment.desc')}
      />
    </View>
  )
}
